// Reports — analytical slices off cms.bookings:
//   - GMV by month (6m, bar chart) — back-compat
//   - Top vendors by GMV (90d, table)
//   - Top cars by booking count (90d, table)
//   - Booking funnel (30d, status counts)
//   - Revenue by country (90d, table)

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import { BarChart, KPI, PageHeader, ToolBtn } from '@drivecars/admin-ui';
import {
  downloadReportsCsv,
  fetchAdminReports,
  type AdminReportCountry,
  type AdminReportTopCar,
  type AdminReportTopVendor,
} from '@/lib/admin-api';

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(v);

const COUNTRY_LABELS: Record<string, string> = {
  ma: 'Morocco',
  ae: 'United Arab Emirates',
  gb: 'United Kingdom',
  us: 'United States',
  es: 'Spain',
  fr: 'France',
};

const VENDOR_COLS: DataTableColumn<AdminReportTopVendor>[] = [
  {
    key: 'name',
    h: 'Vendor',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.name}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.email}
        </div>
      </div>
    ),
  },
  {
    key: 'count',
    h: 'Bookings',
    right: true,
    cell: (r) => <span className="tabular-nums">{r.count.toLocaleString()}</span>,
  },
  {
    key: 'gmv',
    h: 'GMV',
    right: true,
    cell: (r) => (
      <span className="tabular-nums font-semibold">{fmtMoney(r.gmv)}</span>
    ),
  },
];

const CAR_COLS: DataTableColumn<AdminReportTopCar>[] = [
  {
    key: 'title',
    h: 'Car',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.title}</div>
        <div className="truncate font-mono text-[11.5px] text-[color:var(--bo-mute)]">
          {r.slug}
        </div>
      </div>
    ),
  },
  {
    key: 'count',
    h: 'Bookings',
    right: true,
    cell: (r) => <span className="tabular-nums">{r.count.toLocaleString()}</span>,
  },
  {
    key: 'gmv',
    h: 'GMV',
    right: true,
    cell: (r) => (
      <span className="tabular-nums font-semibold">{fmtMoney(r.gmv)}</span>
    ),
  },
];

const COUNTRY_COLS: DataTableColumn<AdminReportCountry>[] = [
  {
    key: 'country',
    h: 'Country',
    cell: (r) => (
      <span className="font-medium">
        {r.country
          ? (COUNTRY_LABELS[r.country] ?? r.country.toUpperCase())
          : '—'}
      </span>
    ),
  },
  {
    key: 'count',
    h: 'Bookings',
    right: true,
    cell: (r) => <span className="tabular-nums">{r.count.toLocaleString()}</span>,
  },
  {
    key: 'gmv',
    h: 'GMV',
    right: true,
    cell: (r) => (
      <span className="tabular-nums font-semibold">{fmtMoney(r.gmv)}</span>
    ),
  },
];

export function Reports() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'reports'],
    queryFn: ({ signal }) => fetchAdminReports(signal),
  });

  const monthly = data?.monthly ?? [];
  const totalGmv = monthly.reduce((s, m) => s + m.gmv, 0);
  const totalCount = monthly.reduce((s, m) => s + m.count, 0);

  const funnel = data?.funnel ?? {
    draft: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0,
  };
  // Conversion: confirmed / (pending + confirmed + completed + cancelled).
  // 'draft' excluded because drafts haven't entered the funnel yet.
  const funnelTotal =
    funnel.pending + funnel.confirmed + funnel.completed + funnel.cancelled;
  const conversion =
    funnelTotal === 0
      ? 0
      : Math.round(((funnel.confirmed + funnel.completed) / funnelTotal) * 100);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Finance"
        title="Reports"
        sub="Six-month GMV trend plus 90-day vendor / car / country slices."
        actions={
          <ToolBtn icon="download" onClick={() => void downloadReportsCsv()}>
            Export CSV
          </ToolBtn>
        }
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load reports: {(error as Error).message}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPI label="GMV · last 6m" value={isLoading ? '…' : fmtMoney(totalGmv)} />
        <KPI
          label="Bookings · last 6m"
          value={isLoading ? '…' : totalCount.toLocaleString()}
        />
        <KPI
          label="Avg / month"
          value={
            isLoading ? '…' : fmtMoney(monthly.length ? totalGmv / monthly.length : 0)
          }
        />
        <KPI
          label="Conversion · 30d"
          value={isLoading ? '…' : `${conversion}%`}
          hint="confirmed+completed / non-draft"
        />
      </div>

      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
        <div className="text-[14px] font-semibold">GMV by month</div>
        <div className="mt-4 h-[240px]">
          {monthly.length === 0 ? (
            <div className="flex h-full items-center justify-center text-[12.5px] text-[color:var(--bo-mute)]">
              {isLoading ? 'Loading…' : 'No bookings in window.'}
            </div>
          ) : (
            <BarChart
              data={monthly as unknown as Array<Record<string, string | number>>}
              keys={[{ key: 'gmv', color: 'var(--bo-accent)' }]}
              labelKey="month"
            />
          )}
        </div>
      </div>

      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
        <div className="mb-3 text-[14px] font-semibold">Booking funnel · 30d</div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <FunnelTile label="Draft" count={funnel.draft} tone="muted" />
          <FunnelTile label="Pending" count={funnel.pending} tone="warn" />
          <FunnelTile label="Confirmed" count={funnel.confirmed} tone="info" />
          <FunnelTile label="Completed" count={funnel.completed} tone="good" />
          <FunnelTile label="Cancelled" count={funnel.cancelled} tone="bad" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
          <div className="mb-3 text-[14px] font-semibold">
            Top vendors · 90d by GMV
          </div>
          <DataTable<AdminReportTopVendor>
            columns={VENDOR_COLS}
            rows={data?.topVendors ?? []}
            emptyLabel={isLoading ? 'Loading…' : 'No vendor bookings in window.'}
            onRowClick={(r) =>
              void navigate({
                to: '/crew/$id' as never,
                params: { id: String(r.vendorId) } as never,
              })
            }
          />
        </div>

        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
          <div className="mb-3 text-[14px] font-semibold">
            Top cars · 90d by bookings
          </div>
          <DataTable<AdminReportTopCar>
            columns={CAR_COLS}
            rows={data?.topCars ?? []}
            emptyLabel={isLoading ? 'Loading…' : 'No bookings in window.'}
            onRowClick={(r) =>
              void navigate({
                to: '/cars/$id' as never,
                params: { id: String(r.carId) } as never,
              })
            }
          />
        </div>
      </div>

      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
        <div className="mb-3 text-[14px] font-semibold">
          Revenue by country · 90d
        </div>
        <DataTable<AdminReportCountry>
          columns={COUNTRY_COLS}
          rows={data?.byCountry ?? []}
          emptyLabel={isLoading ? 'Loading…' : 'No bookings in window.'}
        />
      </div>
    </div>
  );
}

// Funnel tile — coloured pill with count + label. Tone maps to the booking-
// status palette used elsewhere (warn=pending, info=confirmed, good=completed).
function FunnelTile({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'muted' | 'warn' | 'info' | 'good' | 'bad';
}) {
  const palette: Record<typeof tone, string> = {
    muted: 'border-[color:var(--bo-line)] bg-white text-[color:var(--bo-ink)]',
    warn: 'border-[#fde68a] bg-[#fef9c3] text-[#854d0e]',
    info: 'border-[#bfdbfe] bg-[#eff6ff] text-[#1e3a8a]',
    good: 'border-[#a7f3d0] bg-[#ecfdf5] text-[#065f46]',
    bad: 'border-[#fecaca] bg-[#fff1f2] text-[#9f1239]',
  };
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${palette[tone]}`}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.1em] opacity-80">
        {label}
      </div>
      <div className="mt-1 text-[22px] font-semibold tabular-nums">
        {count.toLocaleString()}
      </div>
    </div>
  );
}
